import React from 'react';
import { Alert, Box, Container } from '@mui/material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { usePermission } from '@/shared/hooks/usePermission';
import ListPageSkeleton from './skeletons/ListPageSkeleton';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { toolbarActionsSx } from './toolbarActionsSx';

export interface ListPageProps {
  title: string;
  permission?: string;
  permissionDeniedMessage?: string;
  /** Optional tabs row rendered above the toolbar. */
  tabList?: React.ReactNode;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  /** Gap between items in the left toolbar cluster. Default 2 (16px). */
  toolbarLeftGap?: number;
  filterBar?: React.ReactNode;
  /**
   * Optional left filter column (e.g. FilterPanel). When provided and non-null,
   * rendered beside the main list content and pushes the table.
   */
  filterPanel?: React.ReactNode;
  /**
   * Set when `filterPanel` is a flush FilterPanel that is currently expanded, so
   * the list surface drops the corners facing it and the two read as one block.
   * Leave unset for side panels that keep their own gap.
   */
  filterPanelOpen?: boolean;
  /**
   * Filter column is its own card (keep adjacent corners, bottom inset). Use
   * with FilterPanel `variant="card"` — e.g. Task list / kanban / calendar.
   */
  filterPanelSeparated?: boolean;
  /**
   * Where vertical overflow scrolls.
   * - `contained` (default): body fills the viewport and scrolls inside ListPage
   *   (sticky table / pagination shells).
   * - `page`: body grows with content; Layout main scrolls. Filter panel then
   *   stretches with the body, so list pages that show a side filter should
   *   stay on `contained`.
   */
  bodyScroll?: 'contained' | 'page';
  loading?: boolean;
  /** Replaces the default list skeleton when `loading` is true. */
  loadingFallback?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
  maxWidth?: 'lg' | 'xl';
  /**
   * Theme spacing above the list body. Profile payslips have no toolbar, so the
   * table would otherwise sit on the tab strip.
   */
  topSpacing?: number;
  /**
   * Sync the AppBar title. Nested shells (Profile tabs) pass false so this list
   * does not replace or clear the parent page title.
   */
  syncPageTitle?: boolean;
}

const ListPage: React.FC<ListPageProps> = ({
  title,
  permission,
  permissionDeniedMessage = 'Bạn không có quyền truy cập trang này.',
  tabList,
  toolbarLeft,
  toolbarRight,
  toolbarLeftGap = 2,
  filterBar,
  filterPanel,
  filterPanelOpen = false,
  filterPanelSeparated = false,
  bodyScroll = 'contained',
  loading = false,
  loadingFallback,
  error,
  children,
  maxWidth = 'xl',
  topSpacing = 0,
  syncPageTitle = true,
}) => {
  usePageTitle(syncPageTitle ? title : undefined);
  const { hasPermission } = usePermission();

  const showToolbar = Boolean(toolbarLeft || toolbarRight);
  const pageScroll = bodyScroll === 'page';

  if (permission && !hasPermission(permission)) {
    return (
      <Container maxWidth={maxWidth}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {permissionDeniedMessage}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        flex: pageScroll ? '1 0 auto' : '1 1 0',
        width: '100%',
        alignSelf: 'stretch',
        ...(pageScroll ? {} : { minHeight: 0 }),
        display: 'flex',
        flexDirection: 'column',
        overflow: pageScroll ? 'visible' : 'hidden',
      }}
    >
      {/* Full-bleed tab strip, flush under the app bar. */}
      {tabList && (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'common.white',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth={maxWidth}>{tabList}</Container>
        </Box>
      )}

      <Container
        maxWidth={maxWidth}
        sx={{
          flex: pageScroll ? '1 0 auto' : '1 1 0',
          width: '100%',
          ...(pageScroll ? {} : { minHeight: 0 }),
          display: 'flex',
          flexDirection: 'column',
          overflow: pageScroll ? 'visible' : 'hidden',
          ...(topSpacing ? { mt: topSpacing } : {}),
        }}
      >
        {showToolbar && (
          <Box
            sx={mergeSx(
              {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mt: 2,
                mb: 1,
                flexShrink: 0,
              },
              toolbarActionsSx,
            )}
          >
            <Box
              sx={{
                display: 'flex',
                gap: toolbarLeftGap,
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              {toolbarLeft}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {toolbarRight}
            </Box>
          </Box>
        )}

        {filterBar && (
          <Box sx={{ mb: 2, flexShrink: 0 }}>
            {filterBar}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flex: pageScroll ? '0 0 auto' : 1,
            ...(pageScroll ? {} : { minHeight: 0 }),
            overflow: pageScroll ? 'visible' : 'hidden',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            ...(filterPanelSeparated && {
              pb: 2,
            }),
          }}
        >
          {filterPanel}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              ...(pageScroll ? {} : { minHeight: 0 }),
              overflow: pageScroll ? 'visible' : 'hidden',
              display: 'flex',
              flexDirection: 'column',
              ...(!filterPanelSeparated && {
                '& > .MuiPaper-root': {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  ...(filterPanelOpen && {
                    borderTopLeftRadius: 0,
                  }),
                },
              }),
            }}
          >
            {loading ? (loadingFallback ?? <ListPageSkeleton />) : children}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ListPage;
